import { useWealthOverview } from '@egw/api'
import { Field, ModalDrawer, PageHeader } from '@egw/components'
import { WEALTH_ROUTES } from '@egw/constants'
import { useTranslation } from '@egw/i18n'
import { InformationCircleIcon } from '@heroicons/react/24/solid'
import { type FC, useState } from 'react'
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton'
import { AccountNameForm } from '../../components'

export interface CheckingDetailsPageProps {
  alias: number
}

export const CheckingDetailsPage: FC<CheckingDetailsPageProps> = ({ alias }) => {
  const [isOpen, setIsOpen] = useState(false)
  const { t } = useTranslation('features-account')
  const { data: wealthOverviewData, isLoading, error, refetch } = useWealthOverview()

  const [nameUpdating, setNameUpdating] = useState<boolean>(false)

  const data = wealthOverviewData?.highYieldChecking?.accounts.find((account) => account.alias === alias)

  return (
    <>
      {isLoading ? (
        <div className="egw-container">
          <SkeletonTheme baseColor="#eeefee" highlightColor="rgba(255, 255, 255, 0.5)">
            <Skeleton count={1} height="100px" className="mb-10" />
            <Skeleton count={5} height="50px" />
          </SkeletonTheme>
        </div>
      ) : null}

      {data ? (
        <>
          <PageHeader
            title={t('account_details.title')}
            subtitle={t(`bank_account_titles.${data.accountTitle}`)}
            backLinkUrl={WEALTH_ROUTES.CHECKING_ACCOUNT_DETAILS.backPath?.replace(':alias', alias.toString()) ?? ''}
          />

          <div className="egw-container pt-0">
            <div className="flex flex-col gap-2 mb-8">
              <div className="border-b border-borderDark pb-3">
                <Field
                  label={t('account_details.account_type')}
                  value={t(`bank_account_titles.${data.accountTitle}`)}
                />
              </div>
              <div className="border-b border-borderDark pb-3">
                <Field isPII label={t('account_details.account_number')} value={data.accountNumber} canCopy />
              </div>
              <div className="border-b border-borderDark pb-3">
                <Field label={t('account_details.routing_number')} value={data.routingNumber} canCopy />
              </div>
              <div className="border-b border-borderDark pb-3">
                <Field label={t('account_details.bank_name')} value={t('account_details.coastal')} />
              </div>
              <div className="pb-3">
                {nameUpdating ? (
                  <SkeletonTheme baseColor="#eeefee" highlightColor="rgba(255, 255, 255, 0.5)">
                    <Skeleton count={1} height="50px" />
                  </SkeletonTheme>
                ) : (
                  <Field
                    label={t('account_details.account_name')}
                    value={data.accountName}
                    onEdit={() => {
                      setIsOpen(true)
                    }}
                  />
                )}
              </div>
            </div>
          </div>
        </>
      ) : null}

      {error ? (
        <div className="egw-container">
          <InformationCircleIcon className="w-12 h-12 fill-red-500 mb-4" />
          <p className="text-error text-2xl font-light mb-10">{t('common.generic_error')}</p>
          <p className="text-body text-xl">{t('checking.error')}</p>
        </div>
      ) : null}

      <ModalDrawer
        title={t('account_details.change_account_name')}
        isOpen={isOpen}
        onClose={() => {
          setIsOpen(false)
        }}
      >
        {data ? (
          <AccountNameForm
            accountType={data.accountType}
            alias={alias}
            accountName={data.accountName}
            onSuccess={async () => {
              setNameUpdating(true)
              setIsOpen(false)
              await refetch().then(() => {
                setNameUpdating(false)
              })
            }}
          />
        ) : null}
      </ModalDrawer>
    </>
  )
}
